import dayjs from "dayjs";

export const priceCalculator = ({
  promotions,
  hotelRooms,
  checkInDate,
  checkOutDate,
  adultCount = 2,
  childCount = 0,
  infantCount = 0,
}) => {
  const applicablePromotions = filterApplicablePromotions(
    promotions,
    checkInDate,
    checkOutDate
  );

  const res = {
    promotionData: {},
    rateplanPrices: {},
  };
  hotelRooms.forEach((hotelRoom) => {
    hotelRoom.pricing.forEach((pricing) => {
      if (
        pricing.adultCount !== adultCount ||
        pricing.childCount !== childCount ||
        pricing.infantCount !== infantCount
      ) {
        return;
      }
      const rateplanPrice = {
        price: 0,
        discount: 0,
        discountPercentage: 0,
        priceAfterDiscount: 0,
      };
      pricing.priceForPax.forEach(({ checkInDate, priceBeforeTax }, i) => {
        const formatedCheckin = dayjs(checkInDate).format("YYYY-MM-DD");

        const suitablePromotions = filterApplicablePromotionsFromExtraConfig(
          applicablePromotions,
          priceBeforeTax,
          hotelRoom,
          hotelRoom.ratePlans[pricing.ratePlanCode]?.id,
          checkInDate
        );
        const { mostApplicablePromotion, highestDiscount } =
          getHighestAndApplicablePromotion(
            suitablePromotions,
            pricing,
            priceBeforeTax,
            i
          );
        console.log({ mostApplicablePromotion, suitablePromotions });
        if (mostApplicablePromotion && highestDiscount) {
          if (!res.promotionData[formatedCheckin]) {
            res.promotionData[formatedCheckin] = [];
          }
          if (
            !res.promotionData[formatedCheckin].includes(
              mostApplicablePromotion.id
            )
          ) {
            res.promotionData[formatedCheckin].push(mostApplicablePromotion.id);
          }
        }
        rateplanPrice.price += priceBeforeTax;
        rateplanPrice.discount += highestDiscount;
      });
      rateplanPrice.price = roundPrice(rateplanPrice.price);
      rateplanPrice.discount = roundPrice(rateplanPrice.discount);
      rateplanPrice.discountPercentage = roundPrice(
        (rateplanPrice.discount / rateplanPrice.price) * 100
      );
      rateplanPrice.priceAfterDiscount = roundPrice(
        rateplanPrice.price - rateplanPrice.discount
      );

      res.rateplanPrices[pricing.ratePlanCode] = rateplanPrice;
    });
    const ratePlanCodes = Object.keys(hotelRoom.ratePlans || {});
    ratePlanCodes.forEach((ratePlanCode) => {
      hotelRoom.ratePlans[ratePlanCode].prices =
        res.rateplanPrices[ratePlanCode];
      hotelRoom.ratePlans[ratePlanCode].pricing = hotelRoom.pricing.find(
        (pricing) =>
          pricing.adultCount === adultCount &&
          pricing.ratePlanCode === ratePlanCode
      );
    });

    // Sort ratePlans by priceAfterDiscount (lowest to highest)
    if (ratePlanCodes.length > 0) {
      const sortedRatePlans = {};

      // Create array of [ratePlanCode, ratePlan] pairs for sorting
      const ratePlanEntries = Object.entries(hotelRoom.ratePlans);

      // Sort by priceAfterDiscount
      ratePlanEntries.sort((a, b) => {
        const priceA = a[1].prices?.priceAfterDiscount || Number.MAX_VALUE;
        const priceB = b[1].prices?.priceAfterDiscount || Number.MAX_VALUE;
        return priceB - priceA;
      });

      // Rebuild the ratePlans object in sorted order
      ratePlanEntries.forEach(([code, plan]) => {
        sortedRatePlans[code] = plan;
      });

      // Replace the original ratePlans with sorted version
      hotelRoom.ratePlans = sortedRatePlans;
    }
  });
  return res;
};

const filterApplicablePromotions = (promotions, checkInDate, checkOutDate) => {
  const stayLengthInDays = dayjs(checkOutDate).diff(checkInDate, "day");
  const daysTillCheckin = dayjs().diff(checkInDate, "day");
  return promotions.filter(
    ({
      lengthOfStay,
      type,
      cutoffDays,
      startDate,
      endDate,
      stayStartDate,
      stayEndDate,
    }) => {
      if (lengthOfStay && lengthOfStay > stayLengthInDays) {
        return false;
      }
      if (type === "late" && cutoffDays && cutoffDays < daysTillCheckin) {
        return false;
      }
      if (type === "early" && cutoffDays && cutoffDays > daysTillCheckin) {
        return false;
      }
      if (startDate && dayjs().diff(startDate, "day") < 0) {
        return false;
      }
      if (endDate && dayjs().diff(endDate, "day") > 0) {
        return false;
      }
      if (stayStartDate && dayjs(checkInDate).diff(stayStartDate, "day") < 0) {
        return false;
      }
      if (stayEndDate && dayjs(checkInDate).diff(stayEndDate, "day") > 0) {
        return false;
      }
      return true;
    }
  );
};

const filterApplicablePromotionsFromExtraConfig = (
  promotions,
  priceBeforeTax,
  hotelRoom,
  ratePlanCode,
  checkInDate
) => {
  const applicablePromotions = [];
  promotions.forEach((promotion) => {
    if (promotion.cutoffAmount && promotion.cutoffAmount > priceBeforeTax) {
      return;
    }
    for (let { type, value } of promotion.PromotionsExtraConfigs) {
      switch (type) {
        case "room_id": {
          if (!value.includes(`${hotelRoom.id}`)) {
            return;
          }
          break;
        }
        case "rateplan_id": {
          if (!value.includes(`${ratePlanCode}`)) {
            return;
          }
          break;
        }
        case "day_of_week": {
          if (
            !value.includes(dayjs(checkInDate).format("dddd").toLowerCase())
          ) {
            return;
          }
          break;
        }
        case "blackout_date": {
          if (value.includes(dayjs(checkInDate).format("YYYY-MM-DD"))) {
            return;
          }
          break;
        }
      }
    }
    applicablePromotions.push(promotion);
  });
  return applicablePromotions;
};

const getHighestAndApplicablePromotion = (
  promotions,
  pricing,
  priceBeforeTax,
  pricingIndex
) => {
  const lowestPriceForNight =
    Math.min(
      ...pricing.priceForPax.map(({ priceBeforeTax }) => priceBeforeTax)
    ) || 0;
  let mostApplicablePromotion = promotions[0];
  let highestDiscount = 0;
  promotions.forEach((promotion) => {
    switch (promotion.discountType) {
      case "percentage": {
        const discount = (priceBeforeTax * promotion.discount) / 100;
        highestDiscount = Math.max(discount, highestDiscount);
        if (highestDiscount === discount) {
          mostApplicablePromotion = promotion;
        }
        break;
      }
      case "fixed_amount": {
        highestDiscount = Math.max(promotion.discount, highestDiscount) / 100;
        if (highestDiscount === promotion.discount) {
          mostApplicablePromotion = promotion;
        }
        break;
      }
      case "nights": {
        if (pricingIndex === 0) {
          highestDiscount = Math.max(lowestPriceForNight, highestDiscount);
          if (highestDiscount === lowestPriceForNight) {
            mostApplicablePromotion = promotion;
          }
        }
        break;
      }
    }
  });
  return {
    mostApplicablePromotion,
    highestDiscount,
  };
};

export const roundPrice = (price, digits) => {
  return +price.toFixed(digits);
};
